/**
 * OverallResultBanner Component
 *
 * Displays a prominent banner showing the overall review status and eligibility indicator.
 * CRITICAL: Must prominently show "REQUIRES HUMAN REVIEW" for all results.
 */

import { AlertTriangle, XCircle, CheckCircle, HelpCircle, Info } from 'lucide-react';
import type { ReviewSummary } from '../../types';

interface OverallResultBannerProps {
  summary: ReviewSummary;
  status: string;
  overallResult?: string;
}

export function OverallResultBanner({ summary, status, overallResult }: OverallResultBannerProps) {
  // Calculate issues needing attention
  const issuesNeedingAttention =
    summary.uncertain + summary.contradictory + summary.not_found;

  // Check for critical exclusion criteria met
  const hasExclusionsMet = summary.exclusions_met > 0;

  // Determine overall eligibility indicator (preliminary only)
  const getPreliminaryIndicator = () => {
    if (hasExclusionsMet) {
      return {
        label: 'EXCLUSION CRITERIA MET',
        bg: 'bg-red-100 dark:bg-red-900/40',
        text: 'text-red-800 dark:text-red-300',
        border: 'border-red-300 dark:border-red-700',
        icon: XCircle,
        iconColor: 'text-red-600 dark:text-red-400',
      };
    }
    if (summary.not_met > 0) {
      return {
        label: 'INCLUSION CRITERIA NOT MET',
        bg: 'bg-red-100 dark:bg-red-900/40',
        text: 'text-red-800 dark:text-red-300',
        border: 'border-red-300 dark:border-red-700',
        icon: XCircle,
        iconColor: 'text-red-600 dark:text-red-400',
      };
    }
    if (issuesNeedingAttention > 0) {
      return {
        label: 'REQUIRES FURTHER REVIEW',
        bg: 'bg-amber-100 dark:bg-amber-900/40',
        text: 'text-amber-800 dark:text-amber-300',
        border: 'border-amber-300 dark:border-amber-700',
        icon: HelpCircle,
        iconColor: 'text-amber-600 dark:text-amber-400',
      };
    }
    if (summary.met === summary.total_criteria) {
      return {
        label: 'PRELIMINARILY ELIGIBLE',
        bg: 'bg-green-100 dark:bg-green-900/40',
        text: 'text-green-800 dark:text-green-300',
        border: 'border-green-300 dark:border-green-700',
        icon: CheckCircle,
        iconColor: 'text-green-600 dark:text-green-400',
      };
    }
    return {
      label: 'REVIEW IN PROGRESS',
      bg: 'bg-gray-100 dark:bg-gray-700',
      text: 'text-gray-800 dark:text-gray-200',
      border: 'border-gray-300 dark:border-gray-600',
      icon: Info,
      iconColor: 'text-gray-600 dark:text-gray-400',
    };
  };

  const indicator = getPreliminaryIndicator();
  const IndicatorIcon = indicator.icon;

  return (
    <div className="space-y-4">
      {/* CRITICAL: Human Review Required Warning */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <AlertTriangle size={32} className="text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-amber-800 dark:text-amber-300 tracking-wide">
              REQUIRES HUMAN REVIEW
            </h2>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              All AI-generated results must be verified by a qualified reviewer before making
              eligibility determinations. Do not rely solely on this automated analysis.
            </p>
          </div>
        </div>
      </div>

      {/* Exclusion Warning (if applicable) */}
      {hasExclusionsMet && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-400 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <XCircle size={28} className="text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-800 dark:text-red-300">
                EXCLUSION CRITERIA MET ({summary.exclusions_met})
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                Patient may be excluded based on {summary.exclusions_met} exclusion criteri
                {summary.exclusions_met === 1 ? 'on' : 'a'}. Verify these findings carefully.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overall Status Card */}
      <div className={`rounded-lg border-2 ${indicator.border} ${indicator.bg} p-4`}>
        <div className="flex items-start justify-between gap-4">
          {/* Preliminary Eligibility Indicator */}
          <div className="flex items-center gap-3">
            <IndicatorIcon size={24} className={indicator.iconColor} />
            <div>
              <h3 className={`text-lg font-semibold ${indicator.text}`}>{indicator.label}</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                Preliminary result - requires verification
              </p>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="flex items-center gap-4">
            <div className="text-center px-3 py-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{summary.met}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Met</div>
            </div>
            <div className="text-center px-3 py-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.not_met}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Not Met</div>
            </div>
            <div className="text-center px-3 py-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{summary.uncertain}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Uncertain</div>
            </div>
          </div>
        </div>

        {/* Issues Needing Attention */}
        {issuesNeedingAttention > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400" />
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {issuesNeedingAttention} criteri
                {issuesNeedingAttention === 1 ? 'on' : 'a'} need attention:
              </span>
              <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                {summary.uncertain > 0 && (
                  <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded">
                    {summary.uncertain} uncertain
                  </span>
                )}
                {summary.contradictory > 0 && (
                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded">
                    {summary.contradictory} contradictory
                  </span>
                )}
                {summary.not_found > 0 && (
                  <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                    {summary.not_found} not found
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Total Criteria */}
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Total criteria evaluated: {summary.total_criteria} (
            {summary.total_criteria - summary.exclusions_met} inclusion,{' '}
            {summary.exclusions_met > 0 ? `${summary.exclusions_met} exclusion met` : 'exclusions checked'}
            )
          </p>
        </div>
      </div>
    </div>
  );
}
